/**
 * PURPOSE: Reglas puras de la vista previa de la planilla FEDUP dentro de la planilla digital (sin React ni red).
 * RESPONSIBILITIES:
 *  - Sí: decidir si el navegador puede mostrar un PDF embebido y construir la URL del visor (parámetros nativos de PDF).
 *  - Sí: validar que lo recibido sea un PDF y clasificar cualquier fallo (409, 404, 400, 5xx, red) en un mensaje claro y reintentable.
 *  - No: no pide nada al backend ni toca el estado de la planilla.
 * COLLABORATORS:
 *  - useFedupPreview.js, FedupPreviewModal.jsx, fedupPreviewRequest.js
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/fedupPreview.utils.js
 */

export const FEDUP_PREVIEW_LABEL = "Vista Planilla";
export const FEDUP_PREVIEW_ARIA_LABEL = "Vista Planilla FEDUP";
export const FEDUP_PREVIEW_TOOLTIP = "Vista Planilla: ver cómo queda la planilla FEDUP con los datos digitalizados";

/** Parámetros del visor nativo de PDF (Chrome/Edge/Firefox): sin barras laterales ni de herramientas, sin barra de desplazamiento y ajustado al ancho. */
export const FEDUP_PREVIEW_VIEWER_PARAMS = "toolbar=0&navpanes=0&scrollbar=0&view=FitH";

/**
 * Vista por defecto de cada deporte. El visor nativo de PDF vive en un marco aparte (chrome-extension://…) del que no se puede
 * leer ni fijar el zoom ni el scroll, así que el zoom y el scroll los controla ESTE componente: el iframe se dimensiona a
 * `ancho_del_contenedor × zoom` y a la altura de la página (el visor lo muestra "ajustado al ancho" sin scroll propio) dentro
 * de un contenedor con scroll propio.
 *  - `zoom`: múltiplo del ancho del contenedor (1 = hoja ajustada al ancho, que es como abría antes de FTR-0058).
 *  - `pageRatio`: alto/ancho de la hoja en puntos (se midió en los PDF reales: 612×1008, 612×792, 612×792 y 1190×841).
 *  - `initialTop`: desplazamiento inicial, como fracción del ancho del iframe (solo al abrir, nunca al actualizar ni al hacer zoom).
 */
export const FEDUP_PREVIEW_VIEWS = Object.freeze({
  basquet: Object.freeze({ label: "básquet", zoom: 0.7, pageRatio: 1008 / 612, initialTop: 0 }),
  futbol: Object.freeze({ label: "fútbol", zoom: 0.7, pageRatio: 792 / 612, initialTop: 0 }),
  futsal: Object.freeze({ label: "futsal", zoom: 0.7, pageRatio: 792 / 612, initialTop: 0 }),
  // Vóley: el zoom actual está bien; solo se baja la vista para que el recuadro del formulario quede pegado arriba
  // (la hoja A3 tiene ~8 % de ancho en blanco antes del recuadro "Nombre de la Competencia").
  voley: Object.freeze({ label: "vóley", zoom: 1, pageRatio: 841 / 1190, initialTop: 0.078 }),
  // Atletismo (planillas de la Universiada): pista en A4 vertical y campo en A4 horizontal.
  atletismo: Object.freeze({ label: "atletismo", zoom: 0.7, pageRatio: 842 / 595, initialTop: 0 }),
  atletismoCampo: Object.freeze({ label: "atletismo", zoom: 1, pageRatio: 595 / 842, initialTop: 0 }),
});
export const FEDUP_PREVIEW_FALLBACK_VIEW = Object.freeze({ label: "", zoom: 1, pageRatio: 792 / 612, initialTop: 0 });

export const FEDUP_ZOOM_MIN = 0.3;
export const FEDUP_ZOOM_MAX = 2.5;
export const FEDUP_ZOOM_STEP = 0.1;

/** Margen que el visor nativo deja alrededor de la hoja (px por lado, medido en Chrome) y holgura vertical para que no aparezca scroll interno. */
const VIEWER_SIDE_MARGIN = 8;
const VIEWER_VERTICAL_SLACK = 24;

export function getPreviewView(sport) {
  return FEDUP_PREVIEW_VIEWS[sport] || FEDUP_PREVIEW_FALLBACK_VIEW;
}

export function clampZoom(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.min(FEDUP_ZOOM_MAX, Math.max(FEDUP_ZOOM_MIN, Math.round(n * 100) / 100));
}

/** Siguiente nivel de zoom (dirección +1 / −1), acotado a [mín, máx] y redondeado a centésimas (sin deriva de coma flotante). */
export function stepZoom(current, direction) {
  return clampZoom(Number(current) + Math.sign(direction) * FEDUP_ZOOM_STEP);
}

/** Tamaño del iframe en px para un contenedor de `containerWidth` px: el ancho escala con el zoom y la altura es la de la hoja entera. */
export function computeFrameSize(containerWidth, zoom, pageRatio) {
  const width = Math.max(0, Math.round(Number(containerWidth) * Number(zoom)));
  if (!width) return { width: 0, height: 0 };
  const pageWidth = Math.max(0, width - 2 * VIEWER_SIDE_MARGIN);
  return { width, height: Math.round(pageWidth * pageRatio + VIEWER_VERTICAL_SLACK) };
}

/** Desplazamiento vertical inicial (px) para un iframe de `frameWidth` px. */
export function computeInitialScrollTop(frameWidth, initialTop) {
  return Math.max(0, Math.round(Number(frameWidth) * Number(initialTop || 0)));
}

export const FEDUP_PREVIEW_MESSAGES = Object.freeze({
  network: "No hay conexión con el servidor. Revisa tu red e inténtalo de nuevo.",
  badRequest: "El identificador del partido no es válido: no se puede generar la planilla.",
  notFound: "No se encontró el partido. Cierra la vista previa y vuelve a abrir la planilla.",
  unavailable: "La planilla FEDUP no está disponible para este partido.",
  server: "El servidor no pudo generar la planilla FEDUP. Inténtalo de nuevo en unos segundos.",
  invalidPdf: "El servidor no devolvió un PDF válido. Inténtalo de nuevo.",
  generic: "No se pudo generar la vista previa. Inténtalo de nuevo.",
});

/** Un tope razonable para un mensaje del backend que se muestra tal cual. */
const MAX_SERVER_MESSAGE = 300;

/**
 * ¿Puede este navegador mostrar un PDF embebido? `navigator.pdfViewerEnabled` es la señal estándar; si el navegador no la
 * expone (versiones antiguas), se recurre al tipo MIME registrado. Tablets y móviles suelen responder que no.
 */
export function canEmbedPdf(nav = typeof navigator !== "undefined" ? navigator : null) {
  if (!nav) return false;
  if (typeof nav.pdfViewerEnabled === "boolean") return nav.pdfViewerEnabled;
  try {
    return Boolean(nav.mimeTypes && nav.mimeTypes["application/pdf"]);
  } catch {
    return false;
  }
}

/** URL del iframe: el blob más el fragmento con los parámetros del visor nativo. */
export function buildPreviewViewerSrc(blobUrl) {
  if (!blobUrl) return "";
  return `${blobUrl}#${FEDUP_PREVIEW_VIEWER_PARAMS}`;
}

/** Axios (CanceledError / ERR_CANCELED) o un AbortError nativo: el usuario cerró antes de terminar. */
export function isRequestCanceled(err) {
  if (!err) return false;
  return err.code === "ERR_CANCELED" || err.name === "CanceledError" || err.name === "AbortError";
}

/** Lee un Blob como texto (FileReader: existe también en jsdom, a diferencia de Blob.text). */
export function readBlobAsText(blob) {
  return new Promise((resolve) => {
    if (!blob || typeof FileReader === "undefined") return resolve("");
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => resolve("");
    reader.readAsText(blob);
  });
}

/** Con responseType "blob" el cuerpo JSON del error llega como Blob: se extrae `msg` si existe. */
async function readServerMessage(data) {
  try {
    const text = typeof data === "string" ? data : await readBlobAsText(data);
    const body = text ? JSON.parse(text) : null;
    const msg = String(body?.msg || "").trim();
    return msg.slice(0, MAX_SERVER_MESSAGE);
  } catch {
    return "";
  }
}

/**
 * Devuelve un Blob listo para el visor o lanza un Error con `kind: "invalidPdf"`.
 * Se fuerza `application/pdf` para que el visor nativo lo abra aunque el servidor no haya enviado el tipo.
 */
export function ensurePdfBlob(data) {
  const looksLikeBlob = Boolean(data) && typeof data.size === "number" && typeof data.type === "string";
  if (!looksLikeBlob || data.size === 0 || (data.type && !/pdf/i.test(data.type))) {
    const err = new Error(FEDUP_PREVIEW_MESSAGES.invalidPdf);
    err.kind = "invalidPdf";
    throw err;
  }
  return data.type ? data : new Blob([data], { type: "application/pdf" });
}

/**
 * Clasifica un fallo de la petición.
 * @returns {Promise<{kind: "canceled"|"network"|"badRequest"|"notFound"|"unavailable"|"server"|"invalidPdf"|"generic", status: number, message: string}>}
 */
export async function classifyFedupPreviewError(err) {
  if (isRequestCanceled(err)) return { kind: "canceled", status: 0, message: "" };
  if (err?.kind === "invalidPdf") {
    return { kind: "invalidPdf", status: 0, message: FEDUP_PREVIEW_MESSAGES.invalidPdf };
  }

  // Axios deja el estado en err.response.status; el servicio de vóley lo reexpone en err.status.
  const status = Number(err?.response?.status ?? err?.status) || 0;

  if (!status) return { kind: "network", status: 0, message: FEDUP_PREVIEW_MESSAGES.network };
  if (status === 409) {
    const serverMessage = String(err?.serverMessage || "").trim().slice(0, MAX_SERVER_MESSAGE) || (await readServerMessage(err?.response?.data));
    return { kind: "unavailable", status, message: serverMessage || FEDUP_PREVIEW_MESSAGES.unavailable };
  }
  if (status === 404) return { kind: "notFound", status, message: FEDUP_PREVIEW_MESSAGES.notFound };
  if (status === 400) return { kind: "badRequest", status, message: FEDUP_PREVIEW_MESSAGES.badRequest };
  if (status >= 500) return { kind: "server", status, message: FEDUP_PREVIEW_MESSAGES.server };
  return { kind: "generic", status, message: FEDUP_PREVIEW_MESSAGES.generic };
}

/** Atributo que marca el visor abierto: los atajos globales de la planilla lo consultan para no dispararse. */
export const FEDUP_PREVIEW_OPEN_ATTR = "data-fedup-preview-dialog";

export function isFedupPreviewOpen(doc = typeof document !== "undefined" ? document : null) {
  if (!doc || typeof doc.querySelector !== "function") return false;
  return doc.querySelector(`[${FEDUP_PREVIEW_OPEN_ATTR}]`) !== null;
}
